import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  attachMaterial,
  attachTool,
  detachMaterial,
  detachTool,
  updateStepMaterial,
} from '@/api/step-attachments'
import { PROJECTS_QUERY_KEY } from './use-projects'
import { patchStepMaterialInTree } from './cache-transforms'
import type {
  AttachMaterialRequest,
  AttachToolRequest,
  ProjectDetail,
  UpdateStepMaterialRequest,
} from '@/api/types'

const detailKey = (projectId: string) => [...PROJECTS_QUERY_KEY, 'detail', projectId]

export const useAttachMaterial = (projectId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ stepId, data }: { stepId: string; data: AttachMaterialRequest }) =>
      attachMaterial(stepId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey(projectId) }),
  })
}

interface StepMaterialCtx {
  previous: ProjectDetail | undefined
}

export const useUpdateStepMaterial = (projectId: string) => {
  const qc = useQueryClient()
  const key = detailKey(projectId)
  return useMutation<
    unknown,
    unknown,
    { stepMaterialId: string; data: UpdateStepMaterialRequest },
    StepMaterialCtx
  >({
    mutationFn: ({ stepMaterialId, data }) => updateStepMaterial(stepMaterialId, data),
    onMutate: async ({ stepMaterialId, data }) => {
      if (data.quantity === undefined) return { previous: undefined }
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<ProjectDetail>(key)
      if (previous) {
        qc.setQueryData<ProjectDetail>(
          key,
          patchStepMaterialInTree(previous, stepMaterialId, { quantity: data.quantity })
        )
      }
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  })
}

export const useDetachMaterial = (projectId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (stepMaterialId: string) => detachMaterial(stepMaterialId),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey(projectId) }),
  })
}

export const useAttachTool = (projectId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ stepId, data }: { stepId: string; data: AttachToolRequest }) =>
      attachTool(stepId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey(projectId) }),
  })
}

export const useDetachTool = (projectId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (stepToolId: string) => detachTool(stepToolId),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey(projectId) }),
  })
}
